import Dialog from '../../../Common/Dialog/Dialog'
import DialogActions from '../../../Common/Dialog/DialogActions'
import DialogButton from '../../../Common/Buttons/DialogButton'
import DialogContent from '../../../Common/Dialog/DialogContent'
import DialogTitle from '../../../Common/Dialog/DialogTitle'
import { useTranslation } from 'react-i18next'
import { Elements } from '@stripe/react-stripe-js'
import type { StripeElementLocale } from '@stripe/stripe-js'
import AddCreditCardForm from './AddCreditCardForm'
import { useAppSelector } from '../../../Common/_hooks/useAppSelector'
import { useCallback, useEffect, useMemo, useState } from 'react'
import stripePaymentElementAppearance, { stripePaymentElementFonts } from '../../../Common/Form/StripePaymentElementAppearance'
import getStripePromise from '../../../Common/Form/StripeLoader'
import { Alert } from '@mui/material'

type StripeLoadStatus = 'loading' | 'ready' | 'failed'

interface Props {
  opened: boolean
  onClose: () => void
}

export default function AddCreditCardDialog(props: Props) {
  const [t] = useTranslation('account-v2')

  const userCurrency = useAppSelector(state => state.get('userData').get('prefs').get('currency'))
  const [formKey, setFormKey] = useState(0)
  const [stripeLoadStatus, setStripeLoadStatus] = useState<StripeLoadStatus>('loading')
  const stripePromise = useMemo(getStripePromise, [])

  useEffect(() => {
    if (!props.opened) {
      return
    }

    let active = true
    setStripeLoadStatus('loading')

    void stripePromise.then((stripe) => {
      if (!active) {
        return
      }

      setStripeLoadStatus(stripe ? 'ready' : 'failed')
    }, () => {
      if (!active) {
        return
      }

      setStripeLoadStatus('failed')
    })

    return () => {
      active = false
    }
  }, [props.opened, stripePromise])

  const resetDialogState = useCallback(() => {
    setFormKey(formKey => formKey + 1)
  }, [])

  return <Dialog
    open={props.opened}
    maxWidth="xs"
    fullWidth
    onClose={props.onClose}
    TransitionProps={{
      onExited: resetDialogState,
    }}
    showCloseButton
  >
    <DialogTitle>{t('Add Credit card')}</DialogTitle>

    {stripeLoadStatus === 'failed' ? <>
      <DialogContent>
        <Alert severity="error">{t('Card payments could not be loaded. Please refresh the page and try again.')}</Alert>
      </DialogContent>
      <DialogActions>
        <DialogButton
          color="secondary"
          onClick={props.onClose}
        >{t('Cancel')}</DialogButton>
      </DialogActions>
    </> : <Elements
      key={formKey}
      stripe={stripePromise}
      options={{
        mode: 'setup',
        currency: userCurrency.toLowerCase(),
        locale: window.LOCALE.replace('_', '-') as StripeElementLocale,
        fonts: stripePaymentElementFonts,
        appearance: stripePaymentElementAppearance
      }}
    >
      <AddCreditCardForm
        onClose={props.onClose}
      />
    </Elements>}

  </Dialog>
}
